// src/components/sat/HideOnDiscard.tsx
//
// Makes "Discard" feel as fast as it actually is.
//
// The discard itself is one POST. What made it feel slow was what came after:
// the surfaces that offer it (the dashboard's ResumeTestCard, /sat) are SERVER
// components, so the only way to clear them was router.refresh() — a full RSC
// re-render of a page that runs a lot of queries of its own. The modal closed
// immediately and then the "test in progress" card just sat there, still
// offering to resume a test that no longer existed, until the server answered.
//
// This wraps that card in a client boundary that can hide itself the moment the
// POST succeeds. router.refresh() still runs, but in the background, where the
// student doesn't wait on it.
'use client'

import { createContext, useCallback, useContext, useState, type ReactNode } from 'react'

type Ctx = { onDiscarded: () => void } | null
const DiscardedContext = createContext<Ctx>(null)

/** Opt-in: DiscardAttemptButton hides its card only when wrapped. */
export function useDiscarded(): Ctx {
  return useContext(DiscardedContext)
}

export default function HideOnDiscard({
  children,
  fallback = null,
}: {
  children: ReactNode
  /**
   * Rendered in place of the children once discarded. Omit it where the card
   * should simply vanish (the dashboard). Supply it where the card is a slot
   * that the server refresh will fill with something else (/sat), so the space
   * doesn't collapse to an empty box for the length of the round trip.
   */
  fallback?: ReactNode
}) {
  const [discarded, setDiscarded] = useState(false)
  const onDiscarded = useCallback(() => setDiscarded(true), [])

  // The server render is authoritative: once router.refresh() lands, this
  // component is unmounted anyway because the card is no longer rendered.
  if (discarded) return <>{fallback}</>

  return <DiscardedContext.Provider value={{ onDiscarded }}>{children}</DiscardedContext.Provider>
}
